import { Icon, Icons } from '@/components/icon';
import { P3 } from '@/components/ui/text';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ContactView } from '../queries/use-contact-views';
import { ContactViewTabMenu } from './contact-view-tab-menu';

interface ContactViewTabsProps {
  views: ContactView[];
  activeViewId: string | null;
  onViewSelect: (viewId: string | null) => void;
  onCreateView: () => void;
  onRenameView: (view: ContactView) => void;
  onDuplicateView: (view: ContactView) => void;
  onDeleteView: (view: ContactView) => void;
  hasUnsavedChanges: boolean;
}

export function ContactViewTabs({
  views,
  activeViewId,
  onViewSelect,
  onCreateView,
  onRenameView,
  onDuplicateView,
  onDeleteView,
  hasUnsavedChanges,
}: ContactViewTabsProps) {
  const { t } = useTranslation('desktop_ui');
  const [openMenuViewId, setOpenMenuViewId] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check scroll position to show/hide fade gradients
  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  // Update scroll state on scroll and on mount/resize
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    updateScrollState();
    container.addEventListener('scroll', updateScrollState);

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [updateScrollState]);

  // Re-check scroll state after views change
  useEffect(() => {
    const timeoutId = setTimeout(updateScrollState, 50);
    return () => clearTimeout(timeoutId);
  }, [views.length, updateScrollState]);

  return (
    <div className="flex min-w-0 select-none items-center gap-1">
      {/* All tab - sticky */}
      <button
        type="button"
        onClick={() => onViewSelect(null)}
        className={cn(
          'flex h-6 shrink-0 cursor-pointer items-center gap-0.5 rounded-md px-2 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary-20/50',
          activeViewId === null
            ? 'bg-primary-5 text-primary-100'
            : 'text-primary-60 hover:text-primary-100',
        )}
      >
        <Icon className="text-sm" name={Icons.Table} />
        <P3 className="font-medium">{t('components.contacts.views.all')}</P3>
      </button>

      {/* Divider after All if there are saved views */}
      {views.length > 0 && (
        <div className="flex h-4 w-2 shrink-0 items-center justify-center">
          <div className="h-4 w-px rounded-full bg-primary-10" />
        </div>
      )}

      {/* Scrollable views list */}
      {views.length > 0 && (
        <div
          ref={scrollContainerRef}
          className={cn(
            'flex min-w-0 items-center gap-1 overflow-x-auto scrollbar-hide',
            canScrollLeft &&
              canScrollRight &&
              '[mask-image:linear-gradient(to_right,transparent_0%,black_16px,black_calc(100%-16px),transparent_100%)]',
            canScrollLeft &&
              !canScrollRight &&
              '[mask-image:linear-gradient(to_right,transparent_0%,black_16px,black_100%)]',
            !canScrollLeft &&
              canScrollRight &&
              '[mask-image:linear-gradient(to_right,black_0%,black_calc(100%-16px),transparent_100%)]',
          )}
        >
          {[...views].reverse().map((view) => {
            const isActive = activeViewId === view.id;
            return (
              <ContactViewTabMenu
                key={view.id}
                open={openMenuViewId === view.id}
                onOpenChange={(open) => setOpenMenuViewId(open ? view.id : null)}
                onRename={() => {
                  setOpenMenuViewId(null);
                  onRenameView(view);
                }}
                onDuplicate={() => {
                  setOpenMenuViewId(null);
                  onDuplicateView(view);
                }}
                onDelete={() => {
                  setOpenMenuViewId(null);
                  onDeleteView(view);
                }}
              >
                <button
                  type="button"
                  onPointerDown={(e) => {
                    if (!isActive) {
                      // Prevent dropdown trigger from handling this event
                      e.preventDefault();
                    }
                  }}
                  onClick={() => {
                    if (!isActive) {
                      onViewSelect(view.id);
                    }
                    // If active, the dropdown will open via trigger
                  }}
                  className={cn(
                    'flex h-6 shrink-0 cursor-pointer items-center gap-0.5 rounded-md px-2 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary-20/50',
                    isActive
                      ? 'bg-primary-5 text-primary-100'
                      : 'text-primary-60 hover:text-primary-100',
                  )}
                >
                  <Icon className="text-sm" name={Icons.Table} />
                  <P3 className="max-w-[120px] truncate font-medium">{view.name}</P3>
                  {isActive && hasUnsavedChanges && (
                    <span className="ml-1 h-1.5 w-1.5 rounded-full bg-highlight-default" />
                  )}
                </button>
              </ContactViewTabMenu>
            );
          })}
        </div>
      )}

      {/* Create new view button */}
      <button
        type="button"
        onClick={onCreateView}
        className="flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-xs outline-none text-primary-60 transition-colors hover:text-primary-100 focus-visible:ring-2 focus-visible:ring-primary-20/50"
        title={t('components.contacts.views.createView')}
      >
        <Icon className="text-s" name={Icons.Plus} />
      </button>
    </div>
  );
}
